/**
 * The app services (W1-16 app-integration; contract app/registry/types.ts `AppServices`; ARCHITECTURE.md §1, §5, §8,
 * §9, §10, §12, §15): the one place where everything is wired.
 *
 *  - Workers (io, audio DSP, analysis) through `createRpcClient`, each spawned on first use (the Library starts
 *    without them). A worker that cannot start (no module workers, blocked by policy) leaves a "feature unavailable"
 *    client: calls reject with a FeatureUnavailableError and the user is told once. The io worker holds nothing
 *    between calls, so after a crash the next call starts a fresh one; the stateful ones report the failure to their
 *    owners (the problems service shows the checker as failed).
 *  - The persist client (required: the editor cannot work without storage; a failure here stops the start-up with
 *    the reason), the audio engine (without Web Audio, or when the browser refuses the AudioContext at its first
 *    use, a silent engine takes over whose transport still runs, so the preview plays without sound;
 *    audioFallback.ts), the skin store (`init` with persist-backed storage and the default-skin setting once the
 *    settings are loaded; `setSkinSounds` on every skin change), the problems service (W2-12a; fed with the same
 *    analysis client and the open chart's store), the texture provider factory (asset bytes from persist, blur in
 *    the io worker, GIF delays from the asset meta), i18n, ui, clipboard, nav and the UiBridge.
 *  - Mirrors: settings <-> editor store (level, layout, return-on-stop) both ways; settings -> audio (volumes,
 *    latency calibration, pitch-preserving rate); the playhead for persisted view state.
 *  - The project session (projectSession.ts) behind `nav` (hash routes open and close charts), with the persist
 *    client's cross-tab events: a handoff request saves and closes the chart (the other tab takes it), a lost lock
 *    drops it without saving and asks to reload; a link, a reload or the editor's "Try again" for a chart another
 *    tab edits offers "Switch to it" / "Edit here instead" (D§5.13). The page being hidden writes the view state at
 *    once.
 *  - `onChange` announces service swaps: the open chart's store, engine and textures, and the timeline host.
 *
 * `services` is created lazily on first use (main.tsx hands `() => services` to the bootstrap, which shows the
 * reason when the start-up fails); `createAppServices(options)` builds independent instances (tests, tools).
 */

import type { MessageRef, Unsubscribe } from '../core/util/types';
import type { AssetId, ProjectId } from '../core/model/types';
import type { AudioEngine } from '../audio/types';
import { createAudioEngine } from '../audio/AudioEngine';
import { isAudioEngineError } from '../audio/errors';
import type { PersistClient } from '../persist/types';
import { createPersistClientEx } from '../persist/client';
import type {
  CreateTextureProvider,
  FrameScheduler,
  LoadedSkin,
  SkinStorage,
  SkinStore,
  TextureProvider,
  TimelineHostApi,
} from '../render/types';
import { frameScheduler } from '../render/frame/FrameScheduler';
import { createTextureProvider } from '../render/preview/textures';
import { placeholderSkin, skinStore } from '../render/skin/SkinStore';
import type { EditorStoreApi } from '../state/types';
import { connectSettingsMirror, editorStore, setPlayheadProvider } from '../state/editorStore';
import type { I18n } from '../i18n/types';
import { i18n as runtimeI18n } from '../i18n/runtime';
import type {
  AnalysisWorkerEvents,
  AnalysisWorkerSchema,
  AudioDspWorkerEvents,
  AudioDspWorkerSchema,
  IoWorkerEvents,
  IoWorkerSchema,
  RpcClient,
  SchemaOf,
} from '../workers/protocol';
import { createRpcClient } from '../workers/rpc';
import type { UiBridge } from '../ui/types';
import { createProblemsService } from '../features/checker/problemsService';
import type {
  AppServices,
  ClipboardService,
  CreateProblemsService,
  KeymapService,
  NavService,
  ProblemsService,
  Registry,
  UiService,
} from './registry/types';
import { registry as appRegistry } from './registry/registry';
import { keymap as appKeymap } from './registry/keymap';
import type { SettingsService, UserSettings } from './settings/types';
import { settings as appSettings, type AppSettings } from './settings/settingsService';
import { createUiBridge, createUiService } from './uiService';
import { createNavService, isShellNavService, type NavServiceDeps, type ShellNavService } from './navService';
import { createClipboardService } from './clipboardService';
import {
  createProjectSession,
  errorMessage,
  persistErrorCode,
  type ProjectSession,
  type ProjectSessionDeps,
} from './projectSession';
import { m } from './session.messages';
import { createSessionMarks } from './sessionMarks';
import {
  defaultUnavailableError,
  FeatureUnavailableError,
  type OptionalFeature,
  type UnavailableErrorFactory,
  type UnavailableFeature,
} from './unavailable';
import { createSilentAudioEngine, hasWebAudio, resilientAudioEngine } from './audioFallback';

export {
  FeatureUnavailableError,
  isFeatureUnavailable,
  type OptionalFeature,
  type UnavailableErrorFactory,
  type UnavailableFeature,
} from './unavailable';
export { createSilentAudioEngine, hasWebAudio, resilientAudioEngine, type ResilientAudioEngine } from './audioFallback';

// ---------------------------------------------------------------------------------------------------------------
// Optional features
// ---------------------------------------------------------------------------------------------------------------

/** Display names of the optional features (namespace 'session'). */
export const FEATURE_LABELS: Readonly<Record<OptionalFeature, string>> = {
  audio: m.featureAudio,
  io: m.featureIo,
  dsp: m.featureDsp,
  analysis: m.featureAnalysis,
  skins: m.featureSkins,
  problems: m.featureProblems,
  textures: m.featureTextures,
};

// ---------------------------------------------------------------------------------------------------------------
// Workers
// ---------------------------------------------------------------------------------------------------------------

/** An RPC client whose worker starts on first use (see the module comment). */
export interface SupervisedRpcClient<S extends SchemaOf<S>, E> extends RpcClient<S, E> {
  /** A worker is running now. */
  readonly running: boolean;
  /** Workers started so far. */
  readonly spawns: number;
  /** Why the worker could not start (null: it can). */
  readonly unavailableReason: string | null;
}

/** User-facing reasons of a supervised client (they reach the "not available" toast and the rejections). */
export interface SupervisedRpcText {
  /** The worker failed to start without saying why. */
  readonly notStarted: () => string;
  /** The client was stopped (the services were disposed). */
  readonly stopped: () => string;
}

/** The reasons in the app's language (the i18n runtime). */
export function rpcText(i18n: Pick<I18n, 't'> = runtimeI18n): SupervisedRpcText {
  return {
    notStarted: () => i18n.t(m.reasonWorkerNotStarted),
    stopped: () => i18n.t(m.reasonWorkerStopped),
  };
}

export interface SupervisedRpcOptions {
  /** After a worker failure, terminate it and start a fresh one on the next call (stateless workers only). */
  readonly respawnOnFailure: boolean;
  readonly onUnavailable: (feature: OptionalFeature, reason: string) => void;
  readonly report?: (message: string, err?: unknown) => void;
  /** Reasons when the browser gave none (default: `rpcText()`, the i18n runtime). */
  readonly text?: SupervisedRpcText;
  /** The rejection of calls that cannot run (default: English developer text). */
  readonly unavailableError?: UnavailableErrorFactory;
}

export function supervisedRpcClient<S extends SchemaOf<S>, E>(
  feature: OptionalFeature,
  spawn: () => RpcClient<S, E>,
  opts: SupervisedRpcOptions,
): SupervisedRpcClient<S, E> {
  let inner: RpcClient<S, E> | null = null;
  let innerOffs: Unsubscribe[] = [];
  let unavailableReason: string | null = null;
  let terminated = false;
  let spawns = 0;
  const listeners = new Map<string, Set<(payload: unknown) => void>>();
  const failures = new Set<(reason: string) => void>();
  const postErrors = new Set<Parameters<RpcClient<S, E>['onPostError']>[0]>();
  const text = opts.text ?? rpcText();
  const makeError = opts.unavailableError ?? defaultUnavailableError;

  const attachEvent = (c: RpcClient<S, E>, event: string): void => {
    innerOffs.push(
      c.on(event as keyof E & string, (payload) => {
        for (const cb of [...(listeners.get(event) ?? [])]) cb(payload);
      }),
    );
  };

  const drop = (c: RpcClient<S, E>): void => {
    for (const off of innerOffs.splice(0)) off();
    if (inner === c) inner = null;
    c.terminate();
  };

  const ensure = (): RpcClient<S, E> | null => {
    if (inner !== null) return inner;
    if (unavailableReason !== null || terminated) return null;
    let c: RpcClient<S, E>;
    try {
      c = spawn();
    } catch (err) {
      unavailableReason = errorMessage(err, text.notStarted());
      opts.onUnavailable(feature, unavailableReason);
      return null;
    }
    spawns++;
    inner = c;
    innerOffs = [];
    for (const event of listeners.keys()) attachEvent(c, event);
    innerOffs.push(
      c.onFailure((reason) => {
        opts.report?.(`${feature} worker failed: ${reason}`);
        for (const cb of [...failures]) cb(reason);
        if (opts.respawnOnFailure && inner === c) drop(c);
      }),
      c.onPostError((f) => {
        for (const cb of [...postErrors]) cb(f);
      }),
    );
    return c;
  };

  const unavailable = (): FeatureUnavailableError => makeError(feature, unavailableReason ?? text.stopped());

  return {
    get running() {
      return inner !== null;
    },
    get spawns() {
      return spawns;
    },
    get unavailableReason() {
      return unavailableReason;
    },
    call(method, params, transfer) {
      const c = ensure();
      if (c === null) return Promise.reject(unavailable());
      return c.call(method, params, transfer);
    },
    post(method, params, transfer) {
      ensure()?.post(method, params, transfer);
    },
    on(event, cb) {
      let set = listeners.get(event);
      if (set === undefined) {
        set = new Set();
        listeners.set(event, set);
        if (inner !== null) attachEvent(inner, event);
      }
      const fn = (payload: unknown): void => {
        cb(payload as E[typeof event]);
      };
      const own = set;
      own.add(fn);
      return () => {
        own.delete(fn);
      };
    },
    onFailure(cb) {
      failures.add(cb);
      return () => {
        failures.delete(cb);
      };
    },
    onPostError(cb) {
      postErrors.add(cb);
      return () => {
        postErrors.delete(cb);
      };
    },
    terminate() {
      terminated = true;
      if (inner !== null) drop(inner);
    },
  };
}

function spawnIo(): RpcClient<IoWorkerSchema, IoWorkerEvents> {
  return createRpcClient<IoWorkerSchema, IoWorkerEvents>(
    new Worker(new URL('../workers/io.worker.ts', import.meta.url), { type: 'module', name: 'phiedit-io' }),
  );
}

function spawnDsp(): RpcClient<AudioDspWorkerSchema, AudioDspWorkerEvents> {
  return createRpcClient<AudioDspWorkerSchema, AudioDspWorkerEvents>(
    new Worker(new URL('../workers/audio.worker.ts', import.meta.url), { type: 'module', name: 'phiedit-audio' }),
  );
}

function spawnAnalysis(): RpcClient<AnalysisWorkerSchema, AnalysisWorkerEvents> {
  return createRpcClient<AnalysisWorkerSchema, AnalysisWorkerEvents>(
    new Worker(new URL('../workers/analysis.worker.ts', import.meta.url), {
      type: 'module',
      name: 'phiedit-analysis',
    }),
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Placeholders for features that could not start (the silent audio engine: audioFallback.ts)
// ---------------------------------------------------------------------------------------------------------------

/** A problems service with nothing to report (the checker could not start). */
export function emptyProblemsService(): ProblemsService {
  return {
    problems: () => [],
    counts: () => ({ fatal: 0, error: 0, warning: 0, tip: 0 }),
    subscribe: () => () => undefined,
    next: () => null,
    rules: () => [],
    checking: () => false,
    fullCheck: () => undefined,
    applyFix: () => Promise.resolve(false),
  };
}

/** A texture provider that has nothing (images are drawn as missing). */
export function nullTextureProvider(): TextureProvider {
  return {
    image: () => null,
    gif: () => null,
    blurredIllustration: () => null,
    shaderSource: () => null,
    video: () => null,
    onReady: () => () => undefined,
    dispose: () => undefined,
  };
}

/**
 * A skin store with the placeholder skin only (skins could not start). `installRefused` is what an install says;
 * switching skins rejects with `unavailableError('skins', reason)`.
 */
export function placeholderSkinStore(
  installRefused: MessageRef,
  reason: string,
  active: () => LoadedSkin = placeholderSkin,
  unavailableError: UnavailableErrorFactory = defaultUnavailableError,
): SkinStore {
  return {
    init: () => Promise.resolve(),
    ready: Promise.resolve(),
    list: () => {
      const s = active();
      return [{ id: s.id, name: s.name, author: '', source: s.source }];
    },
    get active() {
      return active();
    },
    setActive: () => Promise.reject(unavailableError('skins', reason)),
    install: () => Promise.resolve({ ok: false as const, errors: [installRefused] }),
    remove: () => Promise.resolve(),
    subscribe: () => () => undefined,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------------------------------------------

export interface AppServicesOptions {
  /** Required. Default: the persist worker client (`createPersistClientEx`). */
  readonly persist?: () => PersistClient;
  readonly settings?: SettingsService;
  readonly editor?: EditorStoreApi;
  readonly registry?: Registry;
  readonly keymap?: KeymapService;
  readonly i18n?: I18n;
  readonly scheduler?: FrameScheduler;
  /** Worker clients (default: the module workers of src/workers/, started on first use). */
  readonly io?: () => RpcClient<IoWorkerSchema, IoWorkerEvents>;
  readonly dsp?: () => RpcClient<AudioDspWorkerSchema, AudioDspWorkerEvents>;
  readonly analysis?: () => RpcClient<AnalysisWorkerSchema, AnalysisWorkerEvents>;
  readonly audio?: () => AudioEngine;
  readonly skins?: () => SkinStore;
  readonly problems?: CreateProblemsService;
  readonly textures?: CreateTextureProvider;
  readonly ui?: (services: () => AppServices | null) => UiService;
  readonly nav?: (deps: NavServiceDeps) => NavService;
  readonly clipboard?: () => ClipboardService;
  /** Project session options (stores, engine, strictness, view-state timing, recovery download). */
  readonly session?: Pick<
    ProjectSessionDeps,
    'createDocStore' | 'createEngine' | 'strict' | 'verifyEvery' | 'viewSave' | 'downloadRecovery' | 'marks'
  >;
  /** Page lifecycle, reload and hash routing (default: the global window; null in workers and Node). */
  readonly window?: Window | null;
  /** How long the skin store waits for the stored settings (the default skin) before starting with the defaults. */
  readonly skinSettingsWaitMs?: number;
  /** "Switch to it": asks the tab that has a chart open to come forward (default: `requestTabFocus`). */
  readonly requestTabFocus?: (id: ProjectId) => boolean;
  /** Developer diagnostics (default console.warn). */
  readonly report?: (message: string, err?: unknown) => void;
}

/** The services plus the integration's own handles (session, availability, bridge, disposal). */
export interface AppServicesExt extends AppServices {
  readonly session: ProjectSession;
  /** The UiBridge over these services (the shell's App builds its own, refreshed per render; this one is for code
   *  outside React, e.g. dev pages and tools). */
  readonly uiBridge: UiBridge;
  /** Optional features that could not start, in the order they failed. */
  unavailable(): readonly UnavailableFeature[];
  /** Resolves when the skin store finished starting (default skin loaded or replaced by the built-in one). */
  readonly skinsReady: Promise<void>;
  /** Closes the open chart, stops every worker and listener. */
  dispose(): Promise<void>;
}

const SKIN_SETTINGS_WAIT_MS = 3000;

function isDev(): boolean {
  try {
    return import.meta.env.DEV;
  } catch {
    return false;
  }
}

function defaultReport(message: string, err?: unknown): void {
  if (err === undefined) console.warn(`services: ${message}`);
  else console.warn(`services: ${message}`, err);
}

function waitAtMost(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    const done = (): void => {
      clearTimeout(t);
      resolve();
    };
    p.then(done, done);
  });
}

/** The BroadcastChannel on which tabs ask each other to come forward (the Library's protocol, W2-10 tabBus). */
export const TAB_FOCUS_CHANNEL = 'phiedit-library';

/**
 * "Switch to it": asks the tab that has `id` open to come forward. Browsers do not let a page switch tabs, so this
 * posts the Library's focus request ({ kind: 'focus', id } on TAB_FOCUS_CHANNEL); the Library module answers it in
 * every tab (focus, blinking title, a toast). App code cannot import a feature's files, so the one message is
 * repeated here (like src/features/history/otherTab.ts). False when tabs cannot talk to each other here.
 */
export function requestTabFocus(id: ProjectId): boolean {
  if (typeof BroadcastChannel !== 'function') return false;
  let ch: BroadcastChannel;
  try {
    ch = new BroadcastChannel(TAB_FOCUS_CHANNEL);
  } catch {
    return false;
  }
  try {
    ch.postMessage({ kind: 'focus', id });
    return true;
  } catch {
    return false;
  } finally {
    ch.close();
  }
}

/**
 * Counts the `openProject` calls in flight per chart: their callers handle failures (the Library shows its own
 * "Open in another tab" choice), the route-driven opens are handled by the services. A call for the chart the address
 * already shows while it is not open (the editor's "Try again" after a failed reload or link) is a retry of the
 * route, not counted (CR-W1-16-4).
 */
function trackExplicitOpens(
  base: NavService,
  inFlight: Map<ProjectId, number>,
  isRouteRetry: (id: ProjectId) => boolean,
): NavService {
  const wrapped = Object.create(base) as NavService;
  Object.defineProperty(wrapped, 'openProject', {
    enumerable: true,
    value: async (id: ProjectId, ws?: Parameters<NavService['openProject']>[1]): Promise<void> => {
      if (isRouteRetry(id)) {
        await base.openProject(id, ws);
        return;
      }
      inFlight.set(id, (inFlight.get(id) ?? 0) + 1);
      try {
        await base.openProject(id, ws);
      } finally {
        const n = (inFlight.get(id) ?? 1) - 1;
        if (n > 0) inFlight.set(id, n);
        else inFlight.delete(id);
      }
    },
  });
  return wrapped;
}

export function createAppServices(opts: AppServicesOptions = {}): AppServicesExt {
  const report = opts.report ?? defaultReport;
  const win = opts.window === undefined ? (typeof window === 'undefined' ? null : window) : opts.window;
  const editor = opts.editor ?? editorStore;
  const settingsSvc: SettingsService = opts.settings ?? appSettings;
  const i18nRef = opts.i18n ?? runtimeI18n;
  const scheduler = opts.scheduler ?? frameScheduler;
  const offs: Unsubscribe[] = [];

  // Required: storage. A failure here stops the start-up (bootstrap shows the reason).
  const persist = (opts.persist ?? (() => createPersistClientEx()))();

  // ---------------- availability ----------------
  const unavailableList: UnavailableFeature[] = [];
  let announce: ((u: UnavailableFeature) => void) | null = null;
  const markUnavailable = (feature: OptionalFeature, reason: string): void => {
    if (unavailableList.some((u) => u.feature === feature)) return;
    const u: UnavailableFeature = { feature, reason };
    unavailableList.push(u);
    report(`${feature} is not available: ${reason}`);
    announce?.(u);
  };
  const optional = <T>(feature: OptionalFeature, make: () => T, fallback: (reason: string) => T): T => {
    try {
      return make();
    } catch (err) {
      const reason = errorMessage(err, i18nRef.t(m.unknownReason));
      markUnavailable(feature, reason);
      return fallback(reason);
    }
  };
  /** The translated "not available" text, also the message of every FeatureUnavailableError the services make. */
  const unavailableText = (feature: OptionalFeature, reason: string): string =>
    i18nRef.t(m.unavailable, { feature: i18nRef.t(FEATURE_LABELS[feature]), reason });
  const unavailableError: UnavailableErrorFactory = (feature, reason) =>
    new FeatureUnavailableError(feature, reason, unavailableText(feature, reason));

  // ---------------- workers ----------------
  const workerText = rpcText(i18nRef);
  const io = supervisedRpcClient('io', opts.io ?? spawnIo, {
    respawnOnFailure: true,
    onUnavailable: markUnavailable,
    report,
    text: workerText,
    unavailableError,
  });
  const dsp = supervisedRpcClient('dsp', opts.dsp ?? spawnDsp, {
    respawnOnFailure: false,
    onUnavailable: markUnavailable,
    report,
    text: workerText,
    unavailableError,
  });
  const analysis = supervisedRpcClient('analysis', opts.analysis ?? spawnAnalysis, {
    respawnOnFailure: false,
    onUnavailable: markUnavailable,
    report,
    text: workerText,
    unavailableError,
  });

  // ---------------- engines and stores ----------------
  /** Why Web Audio cannot run: the browser's own reason when it gave one, else a translated one. */
  const audioReason = (err: unknown): string => {
    if (isAudioEngineError(err)) {
      const cause: unknown = err.cause;
      if (cause instanceof Error && cause.message !== '') return cause.message;
      return i18nRef.t(m.reasonNoWebAudio);
    }
    return errorMessage(err, i18nRef.t(m.reasonNoWebAudio));
  };
  const audio = optional(
    'audio',
    (): AudioEngine => {
      // The real engine creates its AudioContext lazily: without a constructor it could never play.
      if (opts.audio === undefined && !hasWebAudio()) throw new Error(i18nRef.t(m.reasonNoWebAudio));
      return resilientAudioEngine((opts.audio ?? createAudioEngine)(), {
        describe: audioReason,
        onSilent: (reason) => {
          markUnavailable('audio', reason);
        },
        fallback: (reason) => createSilentAudioEngine(reason, undefined, unavailableError),
        report,
      });
    },
    (reason) => createSilentAudioEngine(reason, undefined, unavailableError),
  );
  const skins = optional('skins', opts.skins ?? (() => skinStore), (reason) =>
    placeholderSkinStore(
      { key: m.unavailable, params: { feature: i18nRef.t(m.featureSkins), reason } },
      reason,
      placeholderSkin,
      unavailableError,
    ),
  );

  // `self` exists once everything below is built; services needed before (ui, problems) read it lazily.
  let self: AppServicesExt | null = null;
  const ui = (opts.ui ?? ((s) => createUiService({ services: s })))(() => self);
  announce = (u) => {
    ui.toast(
      { key: m.unavailable, params: { feature: i18nRef.t(FEATURE_LABELS[u.feature]), reason: u.reason } },
      { kind: 'warning', durationMs: 10_000 },
    );
  };
  for (const u of unavailableList) announce(u);
  const clipboard = (opts.clipboard ?? (() => createClipboardService()))();

  const makeTextures = opts.textures ?? createTextureProvider;
  const session = createProjectSession({
    persist,
    editor,
    audio,
    settings: settingsSvc,
    ui: {
      toast: (msg, o) => ui.toast(msg, o),
      choose: (o) => ui.choose(o),
    },
    tr: (r) => i18nRef.tr(r),
    createTextures: (store) =>
      optional(
        'textures',
        () =>
          makeTextures({
            load: (asset) => persist.getAsset(asset),
            blur: (bytes, maxSide) => io.call('blurIllustration', { bytes, maxSide }),
            gifDelays: (asset: AssetId) => store.doc.assets.get(asset)?.meta?.gifDelaysMs ?? null,
          }),
        () => nullTextureProvider(),
      ),
    strict: opts.session?.strict ?? isDev(),
    report,
    // Kept in the window's localStorage (none without a window: workers, Node).
    marks: opts.session?.marks ?? createSessionMarks(() => (win === null ? null : win.localStorage)),
    ...(opts.session?.createDocStore === undefined ? {} : { createDocStore: opts.session.createDocStore }),
    ...(opts.session?.createEngine === undefined ? {} : { createEngine: opts.session.createEngine }),
    ...(opts.session?.verifyEvery === undefined ? {} : { verifyEvery: opts.session.verifyEvery }),
    ...(opts.session?.viewSave === undefined ? {} : { viewSave: opts.session.viewSave }),
    ...(opts.session?.downloadRecovery === undefined ? {} : { downloadRecovery: opts.session.downloadRecovery }),
  });

  const problems = optional(
    'problems',
    () => (opts.problems ?? createProblemsService)({ analysis, getStore: () => session.store, settings: settingsSvc }),
    () => emptyProblemsService(),
  );

  // ---------------- navigation ----------------
  const explicitOpens = new Map<ProjectId, number>();
  /** The last open the routes asked the session for that failed (which chart a nav failure is about). */
  let lastFailedOpen: { readonly id: ProjectId; readonly err: unknown } | null = null;
  const navDeps: NavServiceDeps = {
    editor: () => editor,
    onError: (_reason, err) => {
      onRouteOpenFailed(err);
    },
    ...(win === null ? {} : { window: win }),
  };
  const baseNav = (opts.nav ?? createNavService)(navDeps);
  const shellNav: ShellNavService | null = isShellNavService(baseNav) ? baseNav : null;
  /** The address shows chart `id`, which is not open (nor opening): a reload, a link or a failed open. */
  const routeShowsClosed = (id: ProjectId): boolean => {
    if (shellNav === null) return false;
    const route = shellNav.route();
    return route.kind === 'project' && route.id === id && session.projectId !== id && shellNav.opening !== id;
  };
  const nav = trackExplicitOpens(baseNav, explicitOpens, routeShowsClosed);
  const focusOtherTab = opts.requestTabFocus ?? requestTabFocus;

  const projectName = async (id: ProjectId): Promise<string> => {
    if (session.store?.projectId === id) return session.store.doc.meta.name;
    try {
      const list = await persist.listProjects(true);
      return list.find((p) => p.id === id)?.name ?? id;
    } catch {
      return id;
    }
  };

  /** Charts whose "Switch to it / Edit here instead" offer is on screen (a route may fail more than once:
   *  hashchange + popstate). */
  const offering = new Set<ProjectId>();

  /** A link, a reload or "Try again" asked for a chart another tab edits: offer to switch there or move it here. */
  const offerTakeOver = async (id: ProjectId): Promise<void> => {
    if (offering.has(id)) return;
    offering.add(id);
    try {
      await takeOverFlow(id);
    } finally {
      offering.delete(id);
    }
  };

  const takeOverFlow = async (id: ProjectId): Promise<void> => {
    const name = await projectName(id);
    const choice = await ui.choose<'switch' | 'here'>({
      title: { key: m.lockedTitle, params: { name } },
      body: { key: m.lockedBody },
      options: [
        { value: 'switch', label: { key: m.lockedSwitch }, description: { key: m.lockedSwitchHelp } },
        { value: 'here', label: { key: m.lockedHere }, description: { key: m.lockedHereHelp } },
      ],
    });
    if (choice === 'switch') {
      // The other tab comes forward (where the browser allows it); this one has nothing to show for the chart.
      const asked = focusOtherTab(id);
      if (routeShowsClosed(id)) nav.goLibrary();
      ui.toast(
        { key: asked ? m.lockedSwitched : m.lockedSwitchManual, params: { name } },
        { kind: 'info', durationMs: 10_000 },
      );
      return;
    }
    // Dismissed: back to the Library (UX-016; the chart stays open in the other tab).
    if (choice !== 'here') {
      if (routeShowsClosed(id)) nav.goLibrary();
      return;
    }
    try {
      if (!(await persist.takeOverLock(id))) {
        ui.toast({ key: m.lockedKept, params: { name } }, { kind: 'info', durationMs: 10_000 });
        return;
      }
      await nav.openProject(id);
    } catch (err) {
      ui.toast(
        { key: m.takeOverFailed, params: { reason: errorMessage(err, i18nRef.t(m.unknownReason)) } },
        { kind: 'error' },
      );
    }
  };

  function onRouteOpenFailed(err: unknown): void {
    // Another tab editing the chart is an expected state with its own dialog: not worth a console warning.
    if (persistErrorCode(err) === 'lockedElsewhere') console.debug('services: the chart is open in another tab');
    else report('a chart could not be opened', err);
    if (persistErrorCode(err) !== 'lockedElsewhere' || shellNav === null) return;
    const last = lastFailedOpen;
    const failed = last !== null && last.err === err ? last.id : null;
    if (failed === null || (explicitOpens.get(failed) ?? 0) > 0) return;
    const route = shellNav.route();
    if (route.kind === 'project' && route.id === failed) void offerTakeOver(failed);
  }

  // ---------------- the services object ----------------
  const changeListeners = new Set<() => void>();
  let timeline: TimelineHostApi | null = null;
  let seen: readonly [unknown, unknown, unknown] = [session.store, session.engine, session.textures];
  const notifyChange = (): void => {
    for (const cb of [...changeListeners]) {
      try {
        cb();
      } catch (err) {
        report('a service-change listener failed', err);
      }
    }
  };
  offs.push(
    session.subscribe(() => {
      const now = [session.store, session.engine, session.textures] as const;
      if (now[0] === seen[0] && now[1] === seen[1] && now[2] === seen[2]) return;
      seen = now;
      notifyChange();
    }),
  );

  let bridge: UiBridge | null = null;
  let skinsReady: Promise<void> = Promise.resolve();

  const instance: AppServicesExt = {
    get store() {
      return session.store;
    },
    editor,
    get engine() {
      return session.engine;
    },
    audio,
    persist,
    io,
    dsp,
    analysis,
    problems,
    skins,
    get textures() {
      return session.textures;
    },
    get timeline() {
      return timeline;
    },
    attachTimeline(host) {
      if (timeline === host) return;
      timeline = host;
      notifyChange();
    },
    scheduler,
    registry: opts.registry ?? appRegistry,
    keymap: opts.keymap ?? appKeymap,
    settings: settingsSvc,
    i18n: i18nRef,
    ui,
    clipboard,
    nav,
    onChange(cb) {
      changeListeners.add(cb);
      return () => {
        changeListeners.delete(cb);
      };
    },
    session,
    get uiBridge() {
      bridge ??= createUiBridge({ services: () => instance });
      return bridge;
    },
    unavailable: () => [...unavailableList],
    get skinsReady() {
      return skinsReady;
    },
    async dispose() {
      for (const off of offs.splice(0).reverse()) {
        try {
          off();
        } catch (err) {
          report('disposing a service connection failed', err);
        }
      }
      await session.dispose();
      const disposable = problems as ProblemsService & { readonly dispose?: () => void };
      if (typeof disposable.dispose === 'function') disposable.dispose();
      io.terminate();
      dsp.terminate();
      analysis.terminate();
      audio.dispose();
      const ex = persist as PersistClient & { readonly dispose?: () => void };
      if (typeof ex.dispose === 'function') ex.dispose();
      changeListeners.clear();
    },
  };
  self = instance;

  // ---------------- settings ----------------
  const attachable = settingsSvc as Partial<Pick<AppSettings, 'attach' | 'loadState'>>;
  if (typeof attachable.attach === 'function' && attachable.loadState === 'idle') {
    attachable.attach(persist).catch((err: unknown) => {
      report('the stored settings could not be loaded', err);
    });
  }
  offs.push(
    connectSettingsMirror(
      {
        read: () => {
          const v = settingsSvc.value;
          return {
            level: v.general.level,
            layout: v.editing.layout,
            returnOnStop: v.audio.spaceStops === 'return',
          };
        },
        write: (p) => {
          if (p.level !== undefined) settingsSvc.set('general', 'level', p.level);
          if (p.layout !== undefined) settingsSvc.set('editing', 'layout', p.layout);
          if (p.returnOnStop !== undefined) settingsSvc.set('audio', 'spaceStops', p.returnOnStop ? 'return' : 'stay');
        },
        subscribe: (cb) =>
          settingsSvc.subscribe(() => {
            cb();
          }),
      },
      editor,
    ),
  );

  // Settings -> audio engine (only what changed).
  let lastAudio: UserSettings['audio'] | null = null;
  const applyAudio = (v: UserSettings): void => {
    const a = v.audio;
    const prev = lastAudio;
    lastAudio = a;
    try {
      if (prev === null || prev.volumes !== a.volumes) audio.setVolumes(a.volumes);
      if (prev === null || prev.latency !== a.latency) audio.setLatency(a.latency);
      if (prev?.preservePitch !== a.preservePitch) audio.setPreservePitch(a.preservePitch);
    } catch (err) {
      report('the audio settings could not be applied', err);
    }
  };
  applyAudio(settingsSvc.value);
  offs.push(settingsSvc.subscribe(applyAudio));

  // ---------------- skins ----------------
  let soundsOf: LoadedSkin | null = null;
  const applySkinSounds = (skin: LoadedSkin): void => {
    if (skin === soundsOf) return;
    soundsOf = skin;
    audio
      .setSkinSounds({
        click: skin.sounds.click?.bytes ?? null,
        drag: skin.sounds.drag?.bytes ?? null,
        flick: skin.sounds.flick?.bytes ?? null,
      })
      .catch((err: unknown) => {
        report(`the sounds of the skin "${skin.name}" could not be decoded`, err);
      });
  };
  offs.push(skins.subscribe(applySkinSounds));
  const storage: SkinStorage = {
    list: () => persist.listSkins(),
    get: (id) => persist.getSkin(id),
    put: (pack) => persist.putSkin(pack),
    remove: (id) => persist.deleteSkin(id),
  };
  let skinsStarted = false;
  let lastDefaultSkin = settingsSvc.value.preview.defaultSkin;
  skinsReady = (async () => {
    await waitAtMost(settingsSvc.ready, opts.skinSettingsWaitMs ?? SKIN_SETTINGS_WAIT_MS);
    lastDefaultSkin = settingsSvc.value.preview.defaultSkin;
    try {
      await skins.init({ storage, defaultId: lastDefaultSkin });
    } catch (err) {
      report('the skin store could not start', err);
      ui.toast(
        { key: m.skinFailed, params: { reason: errorMessage(err, i18nRef.t(m.unknownReason)) } },
        { kind: 'warning' },
      );
    }
    skinsStarted = true;
    applySkinSounds(skins.active);
  })();
  // The default skin chosen elsewhere (another tab's settings, a synced profile) becomes active here too.
  offs.push(
    settingsSvc.subscribe((v) => {
      const id = v.preview.defaultSkin;
      if (id === lastDefaultSkin) return;
      lastDefaultSkin = id;
      if (!skinsStarted || id === null || id === skins.active.id) return;
      skins.setActive(id).catch((err: unknown) => {
        report(`the skin ${id} could not be activated`, err);
      });
    }),
  );

  // ---------------- playhead (persisted view state) ----------------
  setPlayheadProvider(() => {
    const engine = session.engine;
    if (engine === null) return 0;
    try {
      return Math.max(0, engine.tempo.beatF64At(audio.transport.chartTime()));
    } catch {
      return 0;
    }
  });
  offs.push(() => {
    setPlayheadProvider(() => 0);
  });

  // ---------------- sessions, routes, other tabs ----------------
  if (shellNav !== null) {
    offs.push(
      shellNav.attachSession({
        open: async (id) => {
          try {
            await session.open(id);
          } catch (err) {
            lastFailedOpen = { id, err };
            throw err;
          }
        },
        close: async () => {
          await session.close();
        },
      }),
    );
  }

  const onExternal = async (e: Parameters<Parameters<PersistClient['onExternalChange']>[0]>[0]): Promise<void> => {
    const open = session.projectId;
    if (open === null || e.id !== open) return;
    const name = session.store?.doc.meta.name ?? open;
    if (e.kind === 'handoffRequested') {
      // Another tab chose "Edit here instead": save, close and return to the Library (the lock is released). When
      // the close failed the session already said so (its changes come back from the journal): no "all saved".
      const saved = await session.close();
      await nav.closeProject();
      if (saved) ui.toast({ key: m.handedOff, params: { name } }, { kind: 'info', durationMs: 10_000 });
    } else if (e.kind === 'lockLost') {
      // Another tab took the chart over: nothing can be saved from here any more.
      await session.abandon();
      nav.goLibrary();
      const reload = await ui.confirm({
        title: { key: m.lockLostTitle },
        body: { key: m.lockLostBody, params: { name } },
        confirm: { key: m.lockLostReload },
      });
      if (reload) win?.location.reload();
    }
  };
  offs.push(
    persist.onExternalChange((e) => {
      onExternal(e).catch((err: unknown) => {
        report(`handling "${e.kind}" from another tab failed`, err);
      });
    }),
  );

  if (win !== null) {
    const flush = (): void => {
      session.flushView();
    };
    const onHide = (): void => {
      flush();
      session.pageHidden();
    };
    const onShow = (e: PageTransitionEvent): void => {
      if (e.persisted) session.pageShown();
    };
    const onVisibility = (): void => {
      if (win.document.visibilityState === 'hidden') flush();
    };
    win.addEventListener('pagehide', onHide);
    win.addEventListener('pageshow', onShow);
    win.document.addEventListener('visibilitychange', onVisibility);
    offs.push(() => {
      win.removeEventListener('pagehide', onHide);
      win.removeEventListener('pageshow', onShow);
      win.document.removeEventListener('visibilitychange', onVisibility);
    });
  }

  return instance;
}

// ---------------------------------------------------------------------------------------------------------------
// The app's services (lazy singleton)
// ---------------------------------------------------------------------------------------------------------------

let appInstance: AppServicesExt | null = null;

/** The app's services, created on first use. */
export function appServices(): AppServicesExt {
  appInstance ??= createAppServices();
  return appInstance;
}

/** THE services of the app (contracted export). Every member reads the lazily created instance. */
export const services: AppServices = {
  get store() {
    return appServices().store;
  },
  get editor() {
    return appServices().editor;
  },
  get engine() {
    return appServices().engine;
  },
  get audio() {
    return appServices().audio;
  },
  get persist() {
    return appServices().persist;
  },
  get io() {
    return appServices().io;
  },
  get dsp() {
    return appServices().dsp;
  },
  get analysis() {
    return appServices().analysis;
  },
  get problems() {
    return appServices().problems;
  },
  get skins() {
    return appServices().skins;
  },
  get textures() {
    return appServices().textures;
  },
  get timeline() {
    return appServices().timeline;
  },
  attachTimeline: (host) => {
    appServices().attachTimeline(host);
  },
  get scheduler() {
    return appServices().scheduler;
  },
  get registry() {
    return appServices().registry;
  },
  get keymap() {
    return appServices().keymap;
  },
  get settings() {
    return appServices().settings;
  },
  get i18n() {
    return appServices().i18n;
  },
  get ui() {
    return appServices().ui;
  },
  get clipboard() {
    return appServices().clipboard;
  },
  get nav() {
    return appServices().nav;
  },
  onChange: (cb) => appServices().onChange(cb),
};
